// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
    FaConciergeBell,
    FaClock,
    FaSync,
    FaBell,
    FaInfoCircle,
    FaChevronRight,
    FaStar
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { useLanguageContext } from "../../context/languageContext";
import { useNavigate } from "react-router-dom";
import { setServiceRequests } from "../../stores/userInfo/detailsSlice";
import { ReviewModal } from "../../components/ReviewModal";
import { API_URL } from "../../config/api";

const ServiceStatus = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useLanguageContext();

    const serviceRequestsArray = useSelector((state) => state.details.serviceRequests || []);
    const businessId = useSelector((state) => state.business.businessId) || 1;
    const PUBLIC_TOKEN = "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkZW5pcy5taW1pbmlAY2xpY2thbGJhbmlhLmNvbSIsImlhdCI6MTc1MDk4MjU3NywiZXhwIjoxNzUxMjQxNzc3fQ.yQ3xGQEfpj62ruJoLJYOl3966g55OlyofRqjoAHzu";

    const [showConfirmIds, setShowConfirmIds] = useState([]);
    const [loading, setLoading] = useState(false);

    const [hasMounted, setHasMounted] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState(null);

    // Which requests have already had their confirmation flash. A ref, not state: the
    // effect below has to read it without listing it as a dependency - depending on it
    // would re-run the effect the moment it changes, and the cleanup would cancel the
    // very timers that clear the flash, leaving the overlay up for good. It also stops
    // every request flashing again each time the refresh button replaces the array.
    const flashedRequestIds = useRef(new Set());

    useEffect(() => {
        if (!hasMounted && serviceRequestsArray.length > 0) {
            setHasMounted(true);
            return undefined;
        }
        if (!hasMounted) return undefined;

        const arrived = serviceRequestsArray.filter((request) => !flashedRequestIds.current.has(request.id));
        if (arrived.length === 0) return undefined;

        arrived.forEach((request) => flashedRequestIds.current.add(request.id));
        setShowConfirmIds((prev) => [...prev, ...arrived.map((request) => request.id)]);

        const timers = arrived.map((request) =>
            setTimeout(() => {
                setShowConfirmIds((prev) => prev.filter((id) => id !== request.id));
            }, 2500)
        );
        return () => timers.forEach(clearTimeout);
    }, [serviceRequestsArray, hasMounted]);

    const fetchAllRequests = async () => {
        setLoading(true);
        try {
            const fetchPromises = serviceRequestsArray.map((req) =>
                fetch(`${API_URL}/api/order/service/${req.id}`, {
                    headers: {
                        Authorization: `Bearer ${PUBLIC_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }).then((res) => res.ok ? res.json() : null)
            );

            const updatedRequests = (await Promise.all(fetchPromises)).filter(Boolean);
            if (updatedRequests.length > 0) dispatch(setServiceRequests(updatedRequests));
        } catch (error) {
            console.error("Error fetching service requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const s = status?.toLowerCase() || '';
        let colors = 'bg-slate-50 text-slate-600 border-slate-200';
        if (s.includes('pend') || s.includes('request')) colors = 'bg-amber-50 text-amber-700 border-amber-100';
        if (s.includes('progress') || s.includes('active')) colors = 'bg-blue-50 text-blue-700 border-blue-100';
        if (s.includes('don') || s.includes('complet')) colors = 'bg-emerald-50 text-emerald-700 border-emerald-100';

        return (
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${colors}`}>
                {status}
            </span>
        );
    };

    if (serviceRequestsArray.length === 0) {
        return (
            <div className="main-content !pb-0 min-h-screen bg-white flex items-center justify-center p-6 text-sharp">
                <div className="w-full max-w-sm text-center space-y-6 animate-app-reveal">
                    <div className="mx-auto w-20 h-20 flex items-center justify-center rounded-2xl bg-slate-50 border border-slate-100">
                        <FaConciergeBell className="text-2xl text-slate-300" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-xl font-clash text-slate-900 tracking-tight">{t("Stay Effortless")}</h2>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed px-8">
                            {t("You haven't requested any services yet. Our concierge is standing by.")}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate(`/room-service?businessid=${businessId}`)}
                        className="btn-app btn-primary-app w-full text-sm tracking-widest uppercase"
                    >
                        Browse Services
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-white">
            <div className="page-padding py-6 max-w-2xl mx-auto space-y-6">

                {/* Slim Header */}
                <div className="flex items-end justify-between border-b border-slate-100 pb-4">
                    <div className="space-y-0.5">
                        <h1 className="text-xl font-clash text-slate-900 tracking-tight">{t("Service Log")}</h1>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("ID Tracking Active")}</p>
                    </div>
                    <button
                        onClick={fetchAllRequests}
                        disabled={loading}
                        className="text-indigo-600 p-2 active:scale-90 transition-all"
                    >
                        <FaSync size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Dense Requests List */}
                <div className="space-y-4">
                    {serviceRequestsArray.map((request, idx) => (
                        <div
                            key={request.id}
                            className="bg-white border border-slate-100 rounded-xl overflow-hidden animate-app-reveal relative"
                            style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                            <div className="p-4 flex flex-col gap-3">
                                {/* Top Row: ID and Status */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-black text-slate-400">#{request.id}</span>
                                        <StatusBadge status={request.serviceStatus} />
                                    </div>
                                    <div className="flex items-center gap-1.5 text-slate-400">
                                        <FaClock size={10} />
                                        <span className="text-[11px] font-bold">{request.scheduled || "ASAP"}</span>
                                    </div>
                                </div>

                                {/* Items Block - Smart Space Usage */}
                                <div className="space-y-2">
                                    {request.serviceItems?.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between group">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-1 h-1 rounded-full bg-indigo-500"></div>
                                                <span className="text-sm font-bold text-slate-800 leading-none">{item.name}</span>
                                            </div>
                                            <div className="h-px flex-1 bg-slate-50 mx-4 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            <span className="text-xs font-medium text-slate-400 whitespace-nowrap">{item.price} ALL</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Compressed Info Bar */}
                                {(request.description || request.totalPrice > 0) && (
                                    <div className="mt-1 pt-3 border-t border-slate-50 flex items-center justify-between gap-4">
                                        {request.description ? (
                                            <div className="flex items-start gap-2 max-w-[60%]">
                                                <FaInfoCircle className="text-slate-300 mt-0.5" size={10} />
                                                <p className="text-xs text-slate-500 font-medium italic line-clamp-1">"{request.description}"</p>
                                            </div>
                                        ) : <div />}

                                        {request.totalPrice > 0 && (
                                            <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                                                <span className="text-[10px] font-black uppercase text-slate-400">{t("Total")}</span>
                                                <span className="text-sm font-black text-slate-900">{request.totalPrice}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {(request.serviceStatus?.toLowerCase().includes('don') || request.serviceStatus?.toLowerCase().includes('complet')) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedServiceId(request.id);
                                            setReviewModalOpen(true);
                                        }}
                                        className="w-full py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-100 transition-all flex items-center justify-center gap-2 mb-2"
                                    >
                                        <FaStar className="text-amber-400" />
                                        {t("Leave Review")}
                                    </button>
                                )}
                            </div>

                            {/* Ultra-Minimal Interaction Indicator */}
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-100 group-hover:text-indigo-600 transition-colors pointer-events-none">
                                <FaChevronRight size={10} />
                            </div>

                            {/* Precise Overlay */}
                            {showConfirmIds.includes(request.id) && (
                                <div className="absolute inset-0 bg-slate-900/95 z-10 flex items-center justify-center text-white animate-fade-in">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></div>
                                        <span className="text-xs font-black uppercase tracking-[0.4em]">{t("Confirmed")}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Smart Utility Card */}
                <div className="p-5 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white border border-indigo-100 flex items-center justify-center flex-shrink-0 text-indigo-600 shadow-sm">
                        <FaBell size={16} />
                    </div>
                    <div className="space-y-1">
                        <h4 className="text-sm font-bold text-indigo-900 leading-tight tracking-tight">{t("Live Updates Active")}</h4>
                        <p className="text-xs text-indigo-600/70 font-medium leading-relaxed">
                            {t("Your status will update automatically as our team prepares your request. No refresh needed.")}
                        </p>
                    </div>
                </div>
            </div>

            <ReviewModal
                isOpen={reviewModalOpen}
                onClose={() => setReviewModalOpen(false)}
                type="SERVICE"
                serviceId={selectedServiceId}
            />
        </div>
    );
};

export default ServiceStatus;
